import { type FormEvent, type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { type CashHandoverView, type CourierHoldingCash, type CouriersHoldingCash, type PayoutContext } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  CASH_HANDOVER_QUEUE_LIMIT,
  CASH_HANDOVER_QUEUE_STATUSES,
  CASH_HANDOVER_REASON_MAX,
  CASH_HANDOVER_REFERENCE_MAX,
  cashDebtPosition,
  cashHandoverActor,
  cashHandoverAuthority,
  cashHandoverConfirmBlock,
  cashHandoverRejectable,
  cashHandoverStatusTone,
  cashHandoverWithinDebt,
  decideCashHandover,
  formatMarketInstant,
  loadCashHandoverQueue,
  loadCouriersHoldingCash,
  loadPayoutContext,
  marketDisplayName,
  marketTimeZone,
  recordCashHandover,
  summarizeCashHandovers,
  type CashHandoverQueueStatus,
} from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { PersonName } from '../components/person-name';
import { IdRef } from '../components/code-ref';
import { DrawerStack } from '../components/drawer-stack';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, InlineRow, KeyValueList, MoneyInput, Notice, PageHeader, Panel, Select, StatCard, Table, TextInput, FormRow, EmptyState, SectionTitle, Ink, Grid, Text, Stack, FormStack, PanelBody } from '@vello/web-theme';

/**
 * Finance › Dinheiro em mãos (W7-CASH-HANDOVER-CONSOLE, D411; Admin checklist #28) — the courier's cash handover under
 * four eyes, over the D392 A2 routes (`/v1/cash-handovers`, wrapped by `CashHandoverApi`).
 *
 * A courier who delivered a guest-cash order holds the platform's share of that cash (A1 debits his receivable). Until
 * this screen nothing but the connected journey could record him handing it back, so the pilot's cash debt could not be
 * settled from anywhere.
 *
 * - The per-market **queue** (`GET /v1/cash-handovers`, `payout:view`; awaiting confirmation by default) — the market is
 *   chosen from the operator's scope, never typed — with exact per-currency totals (bigint, never summed across
 *   currencies), every status translated, and for each handover who recorded it and who decided it, when (in the
 *   market's time zone) and why.
 * - The **decision** (`POST …/:id/decision`, `payout:settle`) with a mandatory reason. Confirm is **hidden** from whoever
 *   recorded the handover and from the courier himself, with the reason shown — the server refuses `SELF_APPROVAL` /
 *   `SELF_HANDOVER` anyway. The maker may still withdraw (reject) its own record: a rejection moves no money. A
 *   confirmation the ledger no longer carries (`409 HANDOVER_EXCEEDS_DEBT`, the cap re-read at the confirmation) or one
 *   another operator decided first is explained in words and the queue is read again.
 * - The **record** (`POST /v1/cash-handovers`, `payout:create`, the screen's one primary action): the courier is checked
 *   first (`GET /v1/payouts/context` — the only read of the debt the server exposes), so the desk shows the cash debt
 *   the LEDGER carries, what RECORDED handovers already promise of it and what is left (the W6Y-02 netting the server
 *   applies), before an amount is typed in the currency of the courier's market through the design-system `MoneyInput`
 *   — never minor units, never a typed currency. The deposit or receipt reference is required (the proof the
 *   checker will look at) — here and, since D450, by the server (`HANDOVER_REFERENCE_REQUIRED`).
 * - D450 (W7-SWEEP, D414): the market's **couriers holding cash** (`GET /v1/cash-handovers/couriers`) are the starting
 *   list — what each owes, what RECORDED handovers already promise, what is left — and picking one checks him; the
 *   queue names the maker and the checker by their sign-in labels; the server now requires the reference too.
 * - Owner 2026-10-18/19: nothing is drawn below a list. A queue row opens a RIGHT-SIDE DRAWER with the handover (who recorded and decided
 *   it, when, why) and the decision (reason + confirm / reject) in its footer; a courier row of the holding-cash list - or the
 *   "Registrar entrega" header action, which starts empty so an ID can be typed - opens the same RECORD drawer with the courier
 *   checked and the form in it.
 * - A refused or failed read is an ERROR — never an empty queue or a zero total. No native dialog is ever opened.
 */
export function CashHandoversScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const operatorId = me?.id;
  const { mayRecord, mayDecide } = cashHandoverAuthority(grants);
  // Owner 2026-10-12: the market of the queue IS the header scope's city — no second market selector on this screen.
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [status, setStatus] = useState<CashHandoverQueueStatus>('RECORDED');
  const [loaded, setLoaded] = useState<{ market: string; status: CashHandoverQueueStatus } | undefined>(undefined);
  const [rows, setRows] = useState<readonly CashHandoverView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  /** D450: the market's couriers holding cash; `undefined` while loading or with no market; a refusal is an error. */
  const [holders, setHolders] = useState<CouriersHoldingCash | undefined>(undefined);
  const [holdersError, setHoldersError] = useState<string | undefined>(undefined);
  const holdersSeq = useRef(0);
  const [reason, setReason] = useState('');
  /** The handover whose record + decision are open in the drawer. */
  const [openHandover, setOpenHandover] = useState<string | undefined>(undefined);
  /** The record drawer (a courier picked from the list, or empty so an ID can be typed). */
  const [recordOpen, setRecordOpen] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger' | 'warning'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const [courierId, setCourierId] = useState('');
  const [context, setContext] = useState<PayoutContext | undefined>(undefined);
  /** The RECORDED queue of the courier's market, netted out of his debt; `null` when that read failed. */
  const [awaiting, setAwaiting] = useState<readonly CashHandoverView[] | null | undefined>(undefined);
  const [checking, setChecking] = useState(false);
  const [amount, setAmount] = useState<bigint | undefined>(undefined);
  const [reference, setReference] = useState('');
  const [recordMsg, setRecordMsg] = useState<{ tone: 'success' | 'danger' | 'warning'; text: string } | undefined>(undefined);
  const [recordBusy, setRecordBusy] = useState(false);
  /**
   * Review W7H-02: every courier read carries a sequence number, and editing the courier field invalidates the one in
   * flight — an answer for the courier the field no longer names never arms the record (it would post that courier's
   * handover while the screen names another).
   */
  const courierSeq = useRef(0);
  /** Review W7H-03: the queue shown is the LAST one asked for — a slower answer for another market is dropped. */
  const queueSeq = useRef(0);
  /** The market the queue was last asked for (a status change on the same market does not blank the holders list). */
  const queueMarket = useRef<string | undefined>(undefined);

  const money = useCallback(
    (m: { readonly amountMinor: string | bigint; readonly currency: string }): string => {
      const raw = typeof m.amountMinor === 'bigint' ? m.amountMinor.toString() : m.amountMinor;
      return /^-?\d+$/.test(raw) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(raw), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : `${raw} ${m.currency}`;
    },
    [lang],
  );
  const statusLabel = (s: string): string => (v('payments.cash_handover_status', s));
  const refusal = (e: { reasonCode?: string; detail?: string; code: string }, fallbackKey: string): string => {
    return refusalOf('refusal.admin.cash_handover', e) ?? t(fallbackKey, { code: fail(e) });
  };

  /** D450: the couriers of `m` holding cash — the last market asked for wins; a refusal is an error, never "nobody". */
  const loadHolders = useCallback(
    async (m: string, background = false): Promise<void> => {
      const seq = ++holdersSeq.current;
      if (!background) {
        setHolders(undefined);
        setHoldersError(undefined);
      }
      if (m === '') return;
      const res = await loadCouriersHoldingCash(runtime, m);
      if (seq !== holdersSeq.current) return;
      if (res.ok) {
        setHoldersError(undefined);
        setHolders(res.value);
      } else setHoldersError(t('cash.holders.error', { code: fail(res.error) }));
    },
    [runtime, t],
  );

  const load = useCallback(
    async (m: string, s: CashHandoverQueueStatus, background = false): Promise<void> => {
      // The couriers holding cash belong to the market: a status change (or a background tick) keeps them on screen.
      void loadHolders(m, background || queueMarket.current === m);
      queueMarket.current = m;
      const seq = ++queueSeq.current;
      // A background tick keeps the rows on screen; a new market / status starts from a clean queue.
      if (!background) {
        setError(undefined);
        setLoaded({ market: m, status: s });
      }
      if (m === '') {
        setRows([]);
        return; // the queue is per market: ask for one instead of pretending
      }
      if (!background) setRows(undefined);
      const res = await loadCashHandoverQueue(runtime, m, s);
      if (seq !== queueSeq.current) return; // another market or status was asked for since: its answer governs
      if (res.ok) {
        setError(undefined);
        setRows(res.value.handovers);
      } else {
        setRows(undefined);
        setError(t('cash.error.queue', { code: fail(res.error) }));
      }
    },
    [runtime, t, loadHolders],
  );

  // The queue follows the header scope's market and the status by itself, and stays current in the background.
  useEffect(() => {
    void load(market.trim(), status);
  }, [market, status, load]);
  useAutoRefresh(() => load(market.trim(), status, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market.trim() !== '' });

  /** The courier's debt, from the ledger, and the RECORDED handovers of his market that already promise part of it. */
  const readCourier = useCallback(
    async (id: string): Promise<boolean> => {
      const seq = ++courierSeq.current;
      const res = await loadPayoutContext(runtime, id);
      if (seq !== courierSeq.current) return false; // the field moved on: this answer is for another courier
      if (!res.ok) {
        setContext(undefined);
        setAwaiting(undefined);
        setRecordMsg({ tone: 'danger', text: refusal(res.error, 'cash.check.failed') });
        return false;
      }
      setContext(res.value);
      if (res.value.marketId === null) setAwaiting(null);
      else {
        const queue = await loadCashHandoverQueue(runtime, res.value.marketId, 'RECORDED');
        if (seq !== courierSeq.current) return false;
        setAwaiting(queue.ok ? queue.value.handovers : null);
      }
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runtime, t],
  );

  /** Check the typed courier — or, from the couriers-holding-cash list (D450), the one picked there. */
  const checkCourier = async (picked?: string): Promise<void> => {
    const id = (picked ?? courierId).trim();
    if (id === '') return;
    if (picked !== undefined) {
      courierSeq.current += 1; // a read still in flight is for the previous ID
      setCourierId(picked);
    }
    setChecking(true);
    setRecordMsg(undefined);
    setContext(undefined);
    setAwaiting(undefined);
    setAmount(undefined);
    await readCourier(id);
    setChecking(false);
  };

  /** Open the record drawer: for a courier of the list (checked at once) or empty (the ID is typed). */
  const openRecord = (picked?: string): void => {
    courierSeq.current += 1; // a read still in flight is for the previous courier
    setRecordOpen(true);
    setRecordMsg(undefined);
    setContext(undefined);
    setAwaiting(undefined);
    setAmount(undefined);
    setReference('');
    setCourierId(picked ?? '');
    if (picked !== undefined) void checkCourier(picked);
  };

  const position = context !== undefined && awaiting !== undefined && awaiting !== null
    ? cashDebtPosition(context.owed, awaiting, context.driverId, awaiting.length < CASH_HANDOVER_QUEUE_LIMIT)
    : undefined;
  const overDebt = !cashHandoverWithinDebt(amount, position);
  const noDebt = position !== undefined && position.debtMinor === 0n;
  const sameCourier = context !== undefined && context.driverId.trim().toLowerCase() === courierId.trim().toLowerCase();
  const canRecord = context !== undefined && sameCourier && context.currency !== null && amount !== undefined && amount > 0n && !overDebt && !noDebt && reference.trim() !== '';

  const onRecord = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (context === undefined) {
      await checkCourier(); // Enter in the courier field checks the courier first
      return;
    }
    if (!canRecord || amount === undefined) return;
    setRecordBusy(true);
    setRecordMsg(undefined);
    const res = await recordCashHandover(runtime, { courierId: context.driverId, amountMinor: amount, reference });
    setRecordBusy(false);
    if (!res.ok) {
      const code = res.error.reasonCode ?? res.error.detail;
      setRecordMsg({ tone: code === 'FINANCIAL_LIMIT_EXCEEDED' || code === 'HANDOVER_EXCEEDS_DEBT' ? 'warning' : 'danger', text: refusal(res.error, 'cash.record.failed') });
      if (code === 'HANDOVER_EXCEEDS_DEBT') await readCourier(context.driverId);
      return;
    }
    setRecordMsg({ tone: 'success', text: t('cash.record.ok', { amount: money(res.value) }) });
    setAmount(undefined);
    setReference('');
    // Show the maker the queue of the scoped market (where Confirm is — rightly — not offered to it), and what is now
    // promised of the courier's debt.
    setStatus('RECORDED');
    await Promise.all([load(market.trim(), 'RECORDED'), readCourier(context.driverId)]);
  };

  const decide = async (row: CashHandoverView, decision: 'CONFIRM' | 'REJECT'): Promise<void> => {
    if (loaded === undefined || loaded.market === '') return;
    setBusy(true);
    setMsg(undefined);
    const res = await decideCashHandover(runtime, row.handoverId, { decision, reason, market: loaded.market });
    setBusy(false);
    if (res.ok) {
      const own = operatorId !== undefined && row.recordedBy.toLowerCase() === operatorId.toLowerCase();
      setMsg({ tone: 'success', text: t(decision === 'CONFIRM' ? 'cash.confirmed' : own ? 'cash.withdrawn' : 'cash.rejected', { amount: money(row) }) });
      setReason('');
      setOpenHandover(undefined);
      await load(loaded.market, loaded.status);
      if (context !== undefined && context.driverId.toLowerCase() === row.courierId.toLowerCase()) await readCourier(context.driverId);
      return;
    }
    const code = res.error.reasonCode ?? res.error.detail;
    setMsg({ tone: code === 'FINANCIAL_LIMIT_EXCEEDED' || code === 'HANDOVER_EXCEEDS_DEBT' ? 'warning' : 'danger', text: refusal(res.error, 'ui.actionFailed') });
    // A 409 means the world moved under the queue (decided by someone else, or the debt re-read at the confirmation).
    if (res.error.status === 409) await load(loaded.market, loaded.status);
  };

  const totals = rows !== undefined ? summarizeCashHandovers(rows) : [];
  const hasReason = reason.trim() !== '';
  const tz = marketTimeZone(loaded?.market, markets);
  const when = (instant: string | null | undefined): string => formatMarketInstant(instant ?? undefined, tz, lang);
  /** D450: "you", the operator's sign-in label from the queue, else the person's name (a short id only when no name is readable). */
  const actor = (id: string | null, label?: string | null): ReactNode => {
    const who = cashHandoverActor(id, label, operatorId);
    if (who === undefined) return undefined;
    if (who.kind === 'you') return t('cash.actor.you');
    return who.kind === 'label' ? who.text : <PersonName id={id} fallback={who.text} />;
  };
  const holderMoney = (amountMinor: string): string => money({ amountMinor, currency: holders?.currency ?? '' });
  const debtLine = (): string | undefined => {
    if (context === undefined || context.owed === null || context.owed === undefined) return undefined;
    const cur = context.owed.currency;
    if (awaiting === null) return t('cash.debt.onlyLedger', { debt: money({ amountMinor: /^-?\d+$/.test(context.owed.receivableMinor) && BigInt(context.owed.receivableMinor) > 0n ? context.owed.receivableMinor : '0', currency: cur }) });
    if (position === undefined) return undefined;
    return t(position.awaitingComplete ? 'cash.debt.summary' : 'cash.debt.summaryPartial', {
      debt: money({ amountMinor: position.debtMinor, currency: cur }),
      awaiting: money({ amountMinor: position.awaitingMinor, currency: cur }),
      count: String(position.awaitingCount),
      remaining: money({ amountMinor: position.remainingMinor, currency: cur }),
    });
  };
  const debt = debtLine();

  const decisionRow = openHandover !== undefined ? (rows ?? []).find((h) => h.handoverId === openHandover) : undefined;
  const closeDecision = (): void => {
    setOpenHandover(undefined);
    setReason('');
  };
  const closeRecord = (): void => setRecordOpen(false);

  return (
    <>
      <PageHeader
        title={t('cash.title')}
        subtitle={t('cash.subtitle')}
        actions={mayRecord ? <Button variant="primary" onClick={() => openRecord()}>{t('cash.record.title')}</Button> : undefined}
      />

      <Panel><Stack gap={4}>
        <Ink as="p" tone="secondary">{t('cash.explain')}</Ink>
        <form onSubmit={(e) => e.preventDefault()}>
          <FormRow columns="minmax(0, 240px)">
            <Field label={t('cash.field.status')} htmlFor="cash-status">
              <Select id="cash-status" value={status} onChange={(e) => setStatus(e.target.value as CashHandoverQueueStatus)}>
                {CASH_HANDOVER_QUEUE_STATUSES.map((s) => (
                  <option key={s} value={s}>{statusLabel(s)}</option>
                ))}
              </Select>
            </Field>
          </FormRow>
        </form>
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        {!mayRecord && <Notice tone="neutral">{t('cash.record.noPermission')}</Notice>}
      </Stack></Panel>

      {scopeMarket === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
      <>
      {totals.length > 0 && (
        <Grid min="sm" gap={4}>
          {totals.map((tot) => (
            <StatCard key={tot.currency} label={t('cash.kpi.total', { currency: tot.currency })} value={money({ amountMinor: tot.totalMinor, currency: tot.currency })} hint={t('cash.kpi.count', { count: String(tot.count) })} />
          ))}
        </Grid>
      )}

      {msg !== undefined && decisionRow === undefined && (
        <div>
          <Notice tone={msg.tone}>{msg.text}</Notice>
        </div>
      )}

      <Panel padded={false}>
        <PanelBody>
          <SectionTitle>
            {t('cash.queue.title', { status: statusLabel(loaded?.status ?? status) })}
          </SectionTitle>
        </PanelBody>
        {rows !== undefined && rows.length >= CASH_HANDOVER_QUEUE_LIMIT && (
          <PanelBody>
            <Notice tone="warning">{t('cash.queue.truncated', { count: String(CASH_HANDOVER_QUEUE_LIMIT) })}</Notice>
          </PanelBody>
        )}
        <Table
          columns={[
            { key: 'courier', header: t('cash.col.courier'), render: (h: CashHandoverView) => <PersonName id={h.courierId} /> },
            { key: 'amount', header: t('cash.col.amount'), numeric: true, render: (h: CashHandoverView) => <span className="tnum">{money(h)}</span> },
            { key: 'reference', header: t('cash.col.reference'), render: (h: CashHandoverView) => (h.reference !== null && h.reference !== '' ? <span className="tnum">{h.reference}</span> : <Text role="caption" tone="tertiary" block>—</Text>) },
            { key: 'status', header: t('cash.col.status'), render: (h: CashHandoverView) => <Badge tone={cashHandoverStatusTone(h.status)}>{statusLabel(h.status)}</Badge> },
            {
              key: 'recorded',
              header: t('cash.col.recordedBy'),
              render: (h: CashHandoverView) => (
                <span>
                  {actor(h.recordedBy, h.recordedByLabel) ?? '—'}
                  <Text role="caption" tone="tertiary" mono block>{when(h.recordedAt)}</Text>
                </span>
              ),
            },
            {
              key: 'decided',
              header: t('cash.col.decidedBy'),
              render: (h: CashHandoverView) => {
                const who = actor(h.decidedBy, h.decidedByLabel);
                if (who === undefined) return <Text role="caption" tone="tertiary" block>{h.status === 'RECORDED' ? t('cash.actor.pending') : '—'}</Text>;
                return (
                  <span>
                    {who}
                    <Text role="caption" tone="tertiary" mono block>{when(h.decidedAt)}</Text>
                    {h.decisionReason !== null && <Text role="caption" tone="tertiary" block>{h.decisionReason}</Text>}
                  </span>
                );
              },
            },
          ]}
          rows={rows ?? []}
          rowKey={(h) => h.handoverId}
          onRowClick={(h) => { setOpenHandover(h.handoverId); setReason(''); setMsg(undefined); }}
          {...(openHandover !== undefined ? { selectedKey: openHandover } : {})}
          loading={rows === undefined && error === undefined}
          loadingLabel={t('ui.loading')}
          error={error !== undefined ? t('ui.loadFailed') : undefined}
          onRetry={() => void load(market.trim(), status)}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={loaded?.market === '' ? t('cash.holders.chooseMarket') : t('cash.queue.empty')} />}
        />
      </Panel>

      <section aria-label={t('cash.holders.title')}>
        <Panel padded={false}>
          <PanelBody>
            <SectionTitle>{t('cash.holders.title')}</SectionTitle>
            <Ink as="p" tone="secondary">{t('cash.holders.hint')}</Ink>
            {holdersError !== undefined && (
              <div>
                <Notice tone="danger">{holdersError}</Notice>
              </div>
            )}
            {holders !== undefined && !holders.complete && (
              <div>
                <Notice tone="warning">{t('cash.holders.partial')}</Notice>
              </div>
            )}
          </PanelBody>
          <Table
            columns={[
              { key: 'courier', header: t('cash.col.courier'), render: (c: CourierHoldingCash) => <PersonName id={c.courierId} /> },
              { key: 'debt', header: t('cash.holders.col.debt'), numeric: true, render: (c: CourierHoldingCash) => <span className="tnum">{holderMoney(c.debtMinor)}</span> },
              { key: 'awaiting', header: t('cash.holders.col.awaiting'), numeric: true, render: (c: CourierHoldingCash) => <span className="tnum">{holderMoney(c.awaitingMinor)}</span> },
              { key: 'remaining', header: t('cash.holders.col.remaining'), numeric: true, render: (c: CourierHoldingCash) => <span className="tnum">{holderMoney(c.remainingMinor)}</span> },
            ]}
            rows={holders?.couriers ?? []}
            rowKey={(c) => c.courierId}
            {...(mayRecord ? { onRowClick: (c: CourierHoldingCash) => openRecord(c.courierId) } : {})}
            loading={(loaded?.market ?? '') !== '' && holders === undefined && holdersError === undefined}
            loadingLabel={t('ui.loading')}
            error={holdersError !== undefined ? t('ui.loadFailed') : undefined}
            onRetry={() => void loadHolders(loaded?.market ?? '')}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={(loaded?.market ?? '') === '' ? t('cash.holders.chooseMarket') : t('cash.holders.empty')} />}
          />
        </Panel>
      </section>
      </>
      )}

      <Drawer open={decisionRow !== undefined} onClose={closeDecision} labelledBy="cash-decision-title">
        {decisionRow !== undefined && (() => {
          const h = decisionRow;
          const block = cashHandoverConfirmBlock(h, operatorId, mayDecide, selfApproval);
          const own = block === 'OWN_RECORD';
          const decided = actor(h.decidedBy, h.decidedByLabel);
          return (
            <>
              <DrawerHeader titleId="cash-decision-title" title={<PersonName id={h.courierId} />} subtitle={<span className="tnum">{money(h)}</span>} actions={<Badge tone={cashHandoverStatusTone(h.status)}>{statusLabel(h.status)}</Badge>} onClose={closeDecision} closeLabel={t('ui.close')} />
              <DrawerBody>
                <DrawerStack>
                  {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
                  <KeyValueList
                    items={[
                      { label: t('cash.col.courier'), value: <IdRef value={h.courierId} copy full /> },
                      { label: t('cash.col.amount'), value: money(h), mono: true },
                      { label: t('cash.col.reference'), value: h.reference !== null && h.reference !== '' ? h.reference : '—', mono: true },
                      { label: t('cash.col.recordedBy'), value: <InlineRow gap={2} align="center" wrap>{actor(h.recordedBy, h.recordedByLabel) ?? '—'}<Text role="caption" tone="tertiary" mono>{when(h.recordedAt)}</Text></InlineRow> },
                      {
                        label: t('cash.col.decidedBy'),
                        value: decided === undefined
                          ? (h.status === 'RECORDED' ? t('cash.actor.pending') : '—')
                          : <InlineRow gap={2} align="center" wrap>{decided}<Text role="caption" tone="tertiary" mono>{when(h.decidedAt)}</Text><SelfApprovalNote record={h} /></InlineRow>,
                      },
                      ...(h.decisionReason !== null ? [{ label: t('cash.reason'), value: h.decisionReason }] : []),
                    ]}
                  />
                  {h.status === 'RECORDED' && block !== 'NO_PERMISSION' && (
                    <Field label={t('cash.reason')} htmlFor="cash-reason" hint={t('cash.reason.hint')}>
                      <TextInput id="cash-reason" value={reason} maxLength={CASH_HANDOVER_REASON_MAX} placeholder={t('ui.reason.placeholder')} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                  )}
                  {block !== undefined && block !== 'NOT_PENDING' && (
                    <Text role="caption" tone="secondary"><span role="note">{v('payments.cash_handover_decision_block', block)}</span></Text>
                  )}
                </DrawerStack>
              </DrawerBody>
              <DrawerFooter>
                <Button variant="ghost" onClick={closeDecision}>{t('ui.close')}</Button>
                {cashHandoverRejectable(h, mayDecide) && (
                  <Button variant="danger" disabled={busy || !hasReason} onClick={() => void decide(h, 'REJECT')}>{own ? t('cash.withdraw') : t('cash.reject')}</Button>
                )}
                {h.status === 'RECORDED' && block === undefined && (
                  <Button variant="secondary" disabled={busy || !hasReason} onClick={() => void decide(h, 'CONFIRM')}>{t('cash.confirm')}</Button>
                )}
              </DrawerFooter>
            </>
          );
        })()}
      </Drawer>

      <Drawer open={recordOpen && mayRecord} onClose={closeRecord} labelledBy="cash-record-title">
        <DrawerHeader titleId="cash-record-title" title={t('cash.record.title')} subtitle={t('cash.record.hint')} onClose={closeRecord} closeLabel={t('ui.close')} />
        <DrawerBody>
          <FormStack id="cash-record-form" gap={4} onSubmit={(e) => void onRecord(e)} noValidate>
            <FormRow
              action={
                <Button variant="secondary" disabled={checking || courierId.trim() === ''} onClick={() => void checkCourier()}>{t('cash.record.check')}</Button>
              }
            >
              <Field label={t('cash.record.courier')} htmlFor="cash-courier">
                <TextInput
                  id="cash-courier"
                  className="tnum"
                  value={courierId}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => {
                    courierSeq.current += 1; // a read still in flight is for the previous ID
                    setCourierId(e.target.value);
                    setContext(undefined);
                    setAwaiting(undefined);
                    setAmount(undefined);
                  }}
                />
              </Field>
            </FormRow>
            {context !== undefined && context.currency !== null && (
              <Stack gap={2}>
                <Notice tone="neutral">{t('cash.record.context', { market: context.marketId !== null ? marketDisplayName(context.marketId, markets) : '—', currency: context.currency })}</Notice>
                {context.marketId !== null && loaded !== undefined && loaded.market !== '' && loaded.market !== context.marketId && (
                  <Notice tone="neutral">{t('cash.record.otherMarket', { market: marketDisplayName(context.marketId, markets) })}</Notice>
                )}
                {context.owed === null || context.owed === undefined ? (
                  <Notice tone="warning">{t('cash.debt.unknown')}</Notice>
                ) : noDebt ? (
                  <Notice tone="neutral">{t('cash.debt.none')}</Notice>
                ) : debt !== undefined ? (
                  <Notice tone="neutral"><span className="tnum">{debt}</span></Notice>
                ) : null}
              </Stack>
            )}
            {context !== undefined && context.currency === null && (
              <div>
                <Notice tone="warning">{t('cash.record.noCurrency')}</Notice>
              </div>
            )}
            {context !== undefined && context.currency !== null && !noDebt && (
              <>
                <MoneyInput
                  id="cash-amount"
                  label={t('cash.amount')}
                  currency={context.currency}
                  locale={lang}
                  valueMinor={amount}
                  onChangeMinor={setAmount}
                  issueText={(issue) => v('format.money_input_issue', issue)}
                  required
                />
                {overDebt && position !== undefined && (
                  <div>
                    <Notice tone="warning">{t('cash.overDebt', { remaining: money({ amountMinor: position.remainingMinor, currency: position.currency }) })}</Notice>
                  </div>
                )}
                <Field label={t('cash.reference')} htmlFor="cash-reference" hint={t('cash.reference.hint')}>
                  <TextInput id="cash-reference" value={reference} maxLength={CASH_HANDOVER_REFERENCE_MAX} autoComplete="off" spellCheck={false} onChange={(e) => setReference(e.target.value)} />
                </Field>
              </>
            )}
            {recordMsg !== undefined && (
              <div>
                <Notice tone={recordMsg.tone}>{recordMsg.text}</Notice>
              </div>
            )}
          </FormStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={closeRecord}>{t('ui.close')}</Button>
          <Button type="submit" form="cash-record-form" variant="primary" disabled={recordBusy || !canRecord}>
            {recordBusy ? t('cash.record.busy') : t('cash.record.submit')}
          </Button>
        </DrawerFooter>
      </Drawer>
    </>
  );
}
